import Dispatcher from "Dispatcher";
import SearchFormActions from "./SearchFormActions";
import MergeActions from "./MergeActions";
import ContextedReduceStore from "./util/ContextedReduceStore";
import typeCheckingStore from "./util/typeCheckingStore";
import PropTypes from "prop-types";
import Q from "lib/Q";
import StereotypesStore from "data/StereotypesStore";
import EntityName from "data/util/EntityName";
import isFieldHidden from "data/util/isFieldHidden";
import sortsToOrder from "lib/sortsToOrder";
import userStore from "data/userStore";
import MappingActions from "./MappingActions";
import MergeStore, {
    isMergeContext,
    MERGE_PLUGINS,
} from "./MergeStore";
import SuperRecordActions from "./SuperRecordActions";
import MapDialogActions from "./MapDialogActions";

/**
 * The following three functions are used for crating the lookup path for each
 * field.
 */
export const buildPathNode = (index, key) => ({index, key});
export const arrayToPath = (arr) =>
    arr.map((node) => (node.index+"."+node.key)).join("/");
export const pathToArray = (path) => {
    if (!path) {
        // Possible rogue mutation of state.fields?
        throw new Error("pathToArray path is 'undefined'.");
    }
    return path.split("/").map((str) => {
        const arr = str.split(".");
        return {index: arr[0], key: arr[1]};
    });
};

const addMissingCheckbox = (field, path) =>
    field.fields.push({
        name: `Missing ${field.name}`,
        stereotypeKey: field.stereotypeKey,
        key: field.key + "missing",
        id: field.id + "missing",
        type: "checkbox",
        path: arrayToPath([...path, buildPathNode(
            field.fields.length,
            field.key + "missing",
        )]),
    });

/**
 * Given raw fields array this function traverse each field adding a lookup path
 * build the correct search fields structure.
 *
 * @param {object} options
 * @param {array} fields
 * @param {array} path
 * @return array
 */
export const buildKeyPaths = (options = {}, fields, path = []) => {
    const newFields = [...fields];
    return newFields.map((field, index) => {
        const newField = {...field};
        const newPath = [...path];
        newPath.push(buildPathNode(index, newField.key)); // array index location for faster lookups
        if (newField.type === "to-one" || newField.type === "to-many") {
            newField.fields = buildKeyPaths(options, newField.fields, newPath);
        } else if (options.useDateRanges && ["date", "daterange", "datetime", "datetimerange"].includes(newField.type)) {
            const baseType = newField.type.replace(/range$/,"");
            if(options.useRelativeDates) {
                // HJC-380 add relative date options to search.
                newField.type = baseType + "relative";
                if (!Array.isArray(newField.fields)) {
                    newField.fields = [{
                        name: `${newField.name}`,
                        key: newField.key + "lastndays",
                        id: newField.id + "lastndays",
                        stereotypeKey: newField.stereotypeKey,
                        type: "string",
                        hasExpectedValues: true,
                        expectedValues: [
                            {label: "Today", value: "today"},
                            {label: "Yesterday", value: "1,days"},
                            {label: "Last 7 days", value: "7,days"},
                            {label: "Last 30 days", value: "30,days"},
                            {label: "Last 90 days", value: "90,days"},
                            {label: "Last 180 days", value: "180,days"},
                        ],
                        expectedValueLookup: {},
                        path: arrayToPath([...newPath, buildPathNode(0, newField.key + "lastndays")])
                    }];
                }
            }
            else {
                // HO-2683 convert all date/datetime to date daterange/datetimerange
                newField.type = baseType + "range";
                if (!Array.isArray(newField.fields)) {
                    // create the date range newFields
                    newField.fields = [{
                        name: `${newField.name} Start`,
                        key: newField.key + "min",
                        id: newField.id + "min",
                        stereotypeKey: newField.stereotypeKey,
                        type: baseType,
                        path: arrayToPath([...newPath, buildPathNode(0, newField.key + "min")])
                    }, {
                        name: `${newField.name} End`,
                        stereotypeKey: newField.stereotypeKey,
                        key: newField.key + "max",
                        id: newField.id + "max",
                        type: baseType,
                        path: arrayToPath([...newPath, buildPathNode(1, newField.key + "max")])
                    }];
                }
            }
        }
        if (typeof options.useMissingCheckbox === "string" && new RegExp(`(^|.*,)${newField.type}(,.*|$)`).test(options.useMissingCheckbox)) {
            if(!Array.isArray(newField.fields)) {
                // wrap it first
                newField.fields = [
                    {
                        ...newField,
                        key: newField.key + "val",
                        id: newField.id + "val",
                        type: newField.type,
                        path: arrayToPath([...newPath, buildPathNode(0, newField.key + "val")])
                    },
                ];
                newField.type += "ormissing";
            }
            addMissingCheckbox(newField, newPath);
        }
        newField.path = arrayToPath(newPath);
        return newField;
    });
};

/**
 * Creates a new state and updates the value at the given path.
 *
 * @param {object} state
 * @param {string} path
 * @param {any} value
 * @return {object}
 */
export const setValueByPath = (state, path, value) => {
    // returns new state with updated value.
    const newState = {...state};
    const updateField = (fields, nodes = []) => {
        const node = nodes[0];
        const newFields = [...fields];
        const newField = {...newFields[node.index]};
        if(node.key === newField.key) {
            if(nodes.length > 1 && Array.isArray(newField.fields)) {
                newField.fields = updateField(newField.fields, nodes.slice(1));
            }
            else if(nodes.length > 1) {
                // Possible rogue mutation of state.fields?
                throw new Error(`setValueByPath path not found '${path}'`);
            }
            else {
                if(value === undefined) {
                    // the value should no longer be set
                    delete newField.value;
                }
                else {
                    newField.value = value;
                }
            }
            newFields[node.index] = newField;
        }
        else {
            // Possible rogue mutation of state.fields?
            throw new Error(`setValueByPath key mismatch expected '${newField.key}' to be '${node.key}'`);
        }
        return newFields;
    };
    newState.fields = updateField(newState.fields, pathToArray(path));
    return newState;
};

/**
 * Given the current state this finds and returns the value at the given path.
 * returns null when no value is set.
 *
 * @param {object} state
 * @param {string} path
 * @return {any}
 */
export const getValueByPath = (state, path) => {
    // returns new state with updated value.
    const findFieldByPath = (fields, nodes = []) => {
        const node = nodes[0];
        const field = fields[node.index];
        if(node.key === field.key) {
            if (nodes.length > 1 && Array.isArray(field.fields)) {
                return findFieldByPath(field.fields, nodes.slice(1));
            }
            else if (nodes.length > 1) {
                // Possible rogue mutation of state.fields?
                throw new Error(`getValueByPath path not found '${path}'`);
            }
            return field;
        }
        else {
            // Possible rogue mutation of state.fields?
            throw new Error(`getValueByPath key mismatch expected '${field.key}' to be '${node.key}'`);
        }
    };
    const value = findFieldByPath(state.fields, pathToArray(path)).value;
    return value !== undefined ? value : null;
};

/**
 * Creates a new state based on the previous one with all field values removed.
 *
 * @param {object} state
 * @return {object}
 */
export const clearFields = (state) => {
    // returns new state with updated value.
    const newState = {...state};
    const updateField = (fields) =>
        fields.map((field) => {
            const newField = {...field};
            if(Array.isArray(newField.fields)) {
                newField.fields = updateField(newField.fields);
                return newField;
            }
            else if(newField.value != null) {
                delete newField.value;
                return newField;
            }
            return field;
        });
    newState.fields = updateField(newState.fields);
    return newState;
};


/**
 * Given the current state this find the first field with a value and
 * return true otherwise false.
 *
 * @param {object} state
 * @return {boolean}
 */
export const hasValues = state => {
    const findValues = f => {
        if(f.value != null && (f.value.trim ? f.value.trim() : f.value) !== "") {
            return true; // found value exit fast
        }
        if (Array.isArray(f.fields)) {
            for(const i of Object.keys(f.fields)) {
                if(findValues(f.fields[i])) {
                    return true; // found value exit fast
                }
            }
        }
        return false;
    };
    return findValues(state);
};

export const hasFieldType = (state, find) => {
    const findType = f => {
        if(f.type && f.type.indexOf(find) !== -1) {
            return true; // found type exit fast
        }
        if (Array.isArray(f.fields)) {
            for(const i of Object.keys(f.fields)) {
                if(findType(f.fields[i])) {
                    return true; // found type exit fast
                }
            }
        }
        return false;
    };
    return findType(state);
};

export const extractSearchToSave = state => {
    const extract = f => {
        if (Array.isArray(f.fields)) {
            let packet = null;
            for (const it of f.fields) {
                const next = extract(it);
                if (next != null) {
                    if (packet == null) packet = {};
                    packet[it.key] = next;
                }
            }
            return packet;
        } else if (f.value != null && f.value !== "") {
            switch (f.type) {
                case "time":
                    return f.value.format("HH:mm:ss");
                case "date":
                    return f.value.format("YYYY-MM-DD");
                case "datetime":
                    return f.value.format("YYYY-MM-DDTHH:mm:ss");
                default:
                    return f.value;
            }
        }
        return null;
    };
    return {
        key: state.key,
        useRelativeDates: state.useRelativeDates,
        fields: extract(state) || {},
    };
};

export const restoreSavedSearch = (state, query) => {
    if (!query || !query.key || !query.fields) return state;
    if (state.availableKeys.indexOf(query.key) < 0) return state;
    const inject = (data, f) => {
        if (data == null || data === "") return f;
        if (Array.isArray(f.fields)) {
            f = {
                ...f,
                fields: f.fields.map(it => {
                    if (data.hasOwnProperty(it.key)) {
                        it = inject(data[it.key], it);
                    }
                    return it;
                })
            };
        } else {
            let value;
            switch (f.type) {
                case "time":
                    value = moment(data, "HH:mm:ss");
                    break;
                case "date":
                    value = moment(data, "YYYY-MM-DD");
                    if (value.year() < 1850) value = null; // never mind
                    break;
                case "datetime":
                    value = moment(data, "YYYY-MM-DDTHH:mm:ss");
                    if (value.year() < 1850) value = null; // never mind
                    break;
                default:
                    value = data;
                    break;
            }
            if (value != null) {
                f = {
                    ...f,
                    value,
                };
            }
        }
        return f;
    };
    state = clearFields(state);
    if (
        state.key !== query.key ||
        state.useRelativeDates !== query.useRelativeDates
    ) {
        state.useRelativeDates = query.useRelativeDates;
        state = selectStereotype({ key: query.key }, state);
    }
    return inject(query.fields, state);
};

export const toQ = state => {
    const convertType = type => {
        switch (type) {
            case "date": return "DATE";
            case "datetime": return "DATETIME";
            case "time": return "TIME";
            case "string": return "STRING";
            case "checkbox": return "BOOLEAN";
            default: throw new Error(`Unknown type: '${type}'`);
        }
    };
    const getSubField = (fs, key) => fs.find(f => f.key === key);
    const subvalue = (fs, key) => {
        const f = getSubField(fs, key);
        if (f == null || !f.value) {
            return null;
        }
        return Q.value(f.value, convertType(f.type));
    };
    const orMissing = (q, f, base) => {
        const includeNulls = subvalue(f.fields, f.key+"missing") === Q.true();
        return base === Q.true()
            ? includeNulls ? q.isNull() : q.isNotNull()
            : includeNulls
                ? q.isNull().or(base)
                : q.isNotNull().and(base);
    };
    const doField = (q, f) => {
        if (! hasValues(f)) {
            return Q.true();
        }
        if (/relative/.test(f.type)) {
            const field = getSubField(f.fields, f.key + "lastndays");
            const value = field.value;
            if(f == null || !value) {
                return orMissing(q, f, Q.true());
            }
            const baseType = f.type.replace(/relative/,"");
            let minValue, maxValue;
            if(value === "today") {
                maxValue = moment();
                minValue = moment().startOf("day");
            }
            else {
                const [v, unit] = value.split(",");
                maxValue = moment().startOf("day");
                // This is old version of momentjs 1.7.2
                minValue = moment().subtract(unit, parseInt(v, 10));
                // moment 2.7.0
                //minValue = maxValue.subtract(parseInt(v, 10), unit); // also note the unit values have changed from 'days' to 'day'
            }
            return orMissing(q, f, q.between(
                Q.value(minValue, convertType(baseType)),
                Q.value(maxValue, convertType(baseType))
            ));
        }
        if (/range$/.test(f.type)) {
            return orMissing(q, f, q.between(
                subvalue(f.fields, f.key+"min"),
                subvalue(f.fields, f.key+"max")
            ));
        }
        if (/ormissing$/.test(f.type)) {
            return orMissing(q, f, doField(q, f.fields[0]));
        }
        switch (f.type) {
            case "integer":
                return q.eq(Q.value(f.value, "INTEGER"));
            case "decimal":
                return q.eq(Q.value(f.value, "DECIMAL"));
            case "time":
                return q.eq(Q.value(f.value, "TIME"));
            case "date":
                return q.eq(Q.value(f.value, "DATE"));
            case "datetime":
                return q.eq(Q.value(f.value, "DATETIME"));
            case "string":
                return q.contains(Q.value(f.value, "STRING"));
            case "to-one":
                return doFields(q, f.fields);
            case "to-many":
                const sub = doFields(Q.ref("it"/*com.armus.script.transform.MapperBinder.ITEM_NAME*/), f.fields);
                return sub === Q.true() ? Q.true() : q.any(sub);
            default:
                throw new Error(`Field '${f.key}' has an unrecognized type: '${f.type}'`);
        }
    };
    const doFields = (base, fields) =>
        Q.conjunction(fields.map(f => {
            const q = base == null ? Q.ref(f.key) : base.dot(f.key);
            return doField(q, f);
        }));
    return doFields(null, state.fields);
};

export const toHuman = state => {
    const IS_MISSING = Object.create(null);
    const formatValue = (value, type) => {
        switch (type) {
            case "integer":
            case "decimal":
            case "string":
            case "checkbox":
                return value;
            case "date":
                return value.format("M/D/YYYY");
            case "datetime":
                return value.format("M/D/YYYY h:mm a");
            case "time":
                return value.format("h:mm a");
            default:
                throw new Error(`Unknown type: '${type}'`);
        }
    };
    const getSubField = (fs, key) =>
        fs.find(f => f.key === key);
    const subvalue = (fs, key) => {
        const f = getSubField(fs, key);
        if (f == null || !f.value) {
            return null;
        }
        return formatValue(f.value, f.type);
    };
    const orMissing = (f, base) => {
        if (!subvalue(f.fields, f.key+"missing")) {
            return base;
        }
        if (base == null) {
            return IS_MISSING;
        }
        return base + " or missing";
    };
    const doField = (prefix, f) => {
        if (!hasValues(f)) {
            return null;
        }
        if (/relative/.test(f.type)) {
            const field = getSubField(f.fields, f.key + "lastndays");
            return orMissing(f, `last ${field.value.replace(/,/, " ")}`);
        }
        if (/range$/.test(f.type)) {
            const min = subvalue(f.fields, f.key+"min");
            const max = subvalue(f.fields, f.key+"max");
            let base;
            if (min == null) {
                if (max == null) base = null;
                else base = "before " + max;
            } else {
                if (max == null) base = "after " + min;
                else base = min + "-" + max;
            }
            return orMissing(f, base);
        }
        if (/ormissing$/.test(f.type)) {
            return orMissing(f, doField(prefix, f.fields[0]));
        }
        switch (f.type) {
            case "integer":
            case "decimal":
            case "time":
            case "date":
            case "datetime":
            case "string":
                return formatValue(f.value, f.type);
            case "to-one":
            case "to-many":
                return doFields(makeLabel(prefix, f.name), f.fields);
            default:
                throw new Error(`Field '${f.key}' has an unrecognized type: '${f.type}'`);
        }
    };
    const makeLabel = (prefix, name) =>
        prefix ? (prefix + " " + name) : name;
    const doFields = (prefix, fields) => fields
        .flatMap(f => {
            const val = doField(prefix, f);
            if (val == null) return null;
            if (val instanceof Array) return val;
            return makeLabel(prefix, f.name)
                + (val === IS_MISSING ? (" is missing") : (": " + val));
        })
        .filter(it => it);
    return doFields(null, state.fields)
        .join(", ");
};

/**
 * Create new array with values copied from sourceFields to destinationFields
 * when matching keys are found.
 *
 * @param {array} destinationFields
 * @param {array} sourceFields
 * @returns {array}
 */
export const copyFieldValuesByKey = (destinationFields, sourceFields) => {
    const newFields = [...destinationFields];
    const updateField = (dFields, findField) =>
        dFields.forEach((field, index) => {
            const newField = {...field};
            if(Array.isArray(newField.fields)) {
                updateField(newField.fields, findField);
            }

            // make sure to check stereotype
            const newKeyPath = newField.stereotypeKey + newField.key;
            const findKeyPath = findField.stereotypeKey + findField.key;

            if( newKeyPath === findKeyPath && // only copy same key
                newField.type === findField.type && // only copy same type values
                (findField.value != null && findField.value !== "") // ignore
            ) {
                newField.value = findField.value;
                dFields[index] = newField;
            }
        });
    const copyValuesToDestination = (fields, dFields) =>
        fields.forEach(field => {
            if(Array.isArray(field.fields)) {
                copyValuesToDestination(field.fields, dFields);
            }
            if(field.value !== null && field.value !== undefined && field.value !== "") {
                // set destination key value
                updateField(dFields, field);
            }
        });
    copyValuesToDestination(sourceFields, newFields);
    return newFields;
};

function selectStereotype(action, contextState) {
    let rawFields, copyValues;
    const stereoKey = action.key || contextState.key;
    const useRelativeDates = action.useRelativeDates !== undefined ?
        action.useRelativeDates : contextState.useRelativeDates;
    if (action.fields) { // a custom schmanky (mapping search)
        rawFields = action.fields;
    } else { // normal stereotype-based thinger
        rawFields = StereotypesStore.getStereotypeFieldsByKey(stereoKey);
        copyValues = contextState.fields;
    }
    const options = {
                    useRelativeDates,
                    useDateRanges: action.useDateRanges !== undefined
                        ? action.useDateRanges
                        : true,
                    // this is searched _after_ range processing
                    useMissingCheckbox: action.useMissingCheckbox !== undefined
                        ? action.useMissingCheckbox
                        : "string,daterange,datetimerange,daterelative,datetimerelative",
    };
                if (EntityName.is(stereoKey)) {
                    rawFields = rawFields.filter(f =>
                        !isFieldHidden(stereoKey, f.key));
                }
    let fields = buildKeyPaths(options, rawFields);
    if (copyValues && Array.isArray(copyValues)) {
        fields = copyFieldValuesByKey(fields, copyValues);
    }
    return {
        ...contextState,
        fields: fields,
        key: stereoKey,
        useRelativeDates,
    };
}

const restoreDefaultSearchOtherwise = (state, otherwiseWork) => {
    const defaultSearch = userStore.getDefaultSearchConfig();
    if (defaultSearch.enabled) {
        return restoreSearchOtherwise(state, defaultSearch.query, otherwiseWork);
    } else {
        return otherwiseWork();
    }
};

const restoreSearchOtherwise = (state, query, otherwiseWork) => {
    if (query &&
        state.availableKeys.indexOf(query.key) >= 0
    ) {
        return restoreSavedSearch({
            ...state,
            page: 1,
            sorts: {},
        }, query);
    } else {
        return otherwiseWork();
    }
};

const restoreDeepLinkOtherwise = (state, otherwiseWork) => {
    let {
        __deepLink: link, // eslint-disable-line prefer-const
        ...next
    } = state;
    next = selectStereotype(link, next);
    const fields = {};
    const addVal = (fs, path, v) => {
        const k = path.shift();
        if (path.length === 0) {
            fs[k] = {
                [`${k}val`]: v,
                [`${k}min`]: v,
                [`${k}max`]: v,
            };
        } else {
            if (!fs.hasOwnProperty(k)) {
                fs[k] = {};
            }
            addVal(fs[k], path, v);
        }
    };
    link.fieldData.forEach(([ k, v ]) =>
        addVal(fields, k.split("."), v));
    const query = {
        key: link.key,
        fields,
    };
    return restoreSavedSearch(next, query);
};

export class SearchFormStore extends ContextedReduceStore {

    getInitialContext() {
        return {
            page: 1, // server index at 1
            pageSize: 25,
            sorts: {},
            fields: [],
            key: "",
            availableKeys: [],
            selectedResult: null,
            useRelativeDates: false,
            __deepLink: null,
        };
    }

    reduceContext(contextState, action) {
        switch (action.type) {

            case SearchFormActions.DEEP_LINKED_SEARCH: {
                return {
                    ...contextState,
                    __deepLink: {
                        key: action.key,
                        fieldData: action.fieldData,
                    },
                };
            }

            case SearchFormActions.INITIALIZE_CONTEXT: {
                if (contextState.key) return contextState;
                contextState = {
                    ...contextState,
                    availableKeys: action.availableKeys,
                };
                const otherwiseWork = () =>
                    selectStereotype(action, contextState);
                if (action.query) {
                    return restoreSearchOtherwise(
                        contextState,
                        action.query,
                        otherwiseWork,
                    );
                } else if (contextState.__deepLink) {
                    action.initiateSearch = true;
                    return restoreDeepLinkOtherwise(
                        contextState,
                        otherwiseWork,
                    );
                } else if (action.useDefaultSearch) {
                    return restoreDefaultSearchOtherwise(
                        contextState,
                        otherwiseWork,
                    );
                } else {
                    return otherwiseWork();
                }
            }

            case SearchFormActions.INITIATE_SEARCH:
            case SearchFormActions.INITIATE_SEARCH_AND_SAVE_DEFAULT: {
                contextState = {
                    ...contextState,
                    page: 1,
                };
                return contextState;
            }
            case SearchFormActions.RESTORE_DEFAULT_AND_INITIATE_SEARCH: {
                return restoreDefaultSearchOtherwise(contextState,
                    () => clearFields(contextState));
            }
            case MergeActions.LAUNCH_DIALOG: {
                return selectStereotype({...action, key: action.target.stereoKey}, contextState);
            }
            case SearchFormActions.SELECT_STEREOTYPE: {
                return selectStereotype(action, contextState);
            }
            case SearchFormActions.SET_USE_RELATIVE_DATES:
                return selectStereotype(action, contextState);
            case SearchFormActions.SET_FIELD:
                return setValueByPath(contextState, action.path, action.value);
            case MergeActions.CANCEL_FOR_SOURCE_OPTLOCK:
            case MergeActions.CANCEL:
            case SearchFormActions.CLEAR_FIELDS:
                return clearFields(contextState);

            case SearchFormActions.SORT: {
                const newContextState = {...contextState};
                let dir = "asc";
                if (action.sort) {
                    dir = action.sort;
                } else {
                    const sorts = newContextState.sorts;
                    if(sorts[action.key]) {
                        if(sorts[action.key] === "desc") {
                            dir = undefined;
                        }
                        else {
                            dir = "desc";
                        }
                    }
                }
                if (dir) {
                    // explicit
                    newContextState.sorts[action.key] = dir;
                } else {
                    delete newContextState.sorts[action.key];
                }
                newContextState.page = 1;
                return newContextState;
            }

            case MapDialogActions.SELECT_PAGE:
            case SearchFormActions.SELECT_PAGE:
                return {...contextState, page: action.page };
            case SearchFormActions.SET_PAGE_SIZE:
                return {...contextState, pageSize: action.pageSize };

            case SearchFormActions.SELECT_RESULT: {
                return {
                    ...contextState,
                    selectedResult: {
                        key: action.result.key,
                        id: action.result.id,
                    },
                };
            }

            default:
                return contextState;
        }
    }

    reduceEachContext(ctx, action, contextId) {
        switch(action.type) {
            case MappingActions.IT_IS_DONE: {
                if (!isMergeContext(contextId)) return ctx;
                if (MergeStore.isReady()) return ctx;
                if (MergeStore.getPluginKey() == MERGE_PLUGINS.GLOBAL_MERGE) return ctx;
                return clearFields(ctx);
            }
            case SuperRecordActions.UPDATED: {
                if (!isMergeContext(contextId)) return ctx;
                if (MergeStore.isReady()) return ctx;
                if (MergeStore.getPluginKey() !== MERGE_PLUGINS.GLOBAL_MERGE) return ctx;
                return clearFields(ctx);
            }
            default:
                return ctx;
        }
    }

    getKey(context) {
        return this.getState(context).key || StereotypesStore.getDefaultKey();
    }

    getAvailableKeys(context) {
        return this.getState(context).availableKeys;
    }

    getFields(context) {
        return this.getState(context).fields;
    }

    getSearchToSave(context) {
        return extractSearchToSave(this.getState(context));
    }

    getSorts(context) {
        return this.getState(context).sorts;
    }

    getPage(context) {
        return this.getState(context).page;
    }

    getPageSize(context) {
        return this.getState(context).pageSize;
    }

    hasValues(context) {
        return hasValues(this.getState(context));
    }

    hasDateFields(context) {
        return hasFieldType(this.getState(context), "date");
    }

    getValueByPath(context, path) {
        return getValueByPath(this.getState(context), path);
    }

    getQ(context) {
        return toQ(this.getState(context));
    }

    getHuman(context) {
        return toHuman(this.getState(context));
    }

    getUseRelativeDates(context) {
        return this.getState(context).useRelativeDates;
    }

    getSearchArgs(context) {
        // key, query, order, pageSize = 100, page = 1
        const s = this.getState(context);
        return [
            s.key,
            toQ(s).toAS(),
            sortsToOrder(s.sorts),
            s.pageSize,
            s.page
        ];
    }

    getSelectedResult(context) {
        return this.getState(context).selectedResult;
    }
}

SearchFormStore.contextTypes = {
    key: PropTypes.string.isRequired,
    page: PropTypes.number,
    pageSize: PropTypes.number,
    sorts: PropTypes.object,
    fields: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        key: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired,
        value: PropTypes.any
    })),
    selectedResult: PropTypes.shape({
        key: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
    }),
};

export default typeCheckingStore(new SearchFormStore(Dispatcher));



// WEBPACK FOOTER //
// ./app/data/SearchFormStore.js